import * as React from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid2";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent, SelectProps } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { Client } from "./data/clients";
import { ClaimType, ClientType } from "./data/types";

export interface ClientFormState {
  values: Partial<Client>;
  errors: Partial<Record<keyof ClientFormState["values"], string>>;
}

export type FormFieldValue = string | string[] | number | boolean | File | null;

export interface ClientFormProps {
  formState: ClientFormState;
  clientTypes: ClientType[];
  claimTypes: ClaimType[];
  onFieldChange: (
    name: keyof ClientFormState["values"],
    value: FormFieldValue,
  ) => void;
  onSubmit: (formValues: Partial<ClientFormState["values"]>) => Promise<void>;
}

const ClientForm = (props: ClientFormProps) => {
  const { formState, clientTypes, claimTypes, onFieldChange, onSubmit } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;

  const handleTextFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof ClientFormState["values"],
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleCheckboxFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onFieldChange(
        event.target.name as keyof ClientFormState["values"],
        checked,
      );
    },
    [onFieldChange],
  );

  const handleSelectFieldChange = React.useCallback(
    (event: SelectChangeEvent) => {
      onFieldChange(
        event.target.name as keyof ClientFormState["values"],
        event.target.value,
      );
    },
    [onFieldChange],
  );

  return (
    <Box
      component="form"
      aria-label="form"
      onSubmit={() => onSubmit(formValues)}
      noValidate
      autoComplete="off"
      sx={{ width: "100%" }}
    >
      <FormGroup>
        <Grid container sx={{ width: "100%", pt: 1 }}>
          <Grid size={{ xs: 12, sm: 12 }} sx={{ display: "flex" }}>
            <TextField
              value={formValues.name ?? ""}
              onChange={handleTextFieldChange}
              name="name"
              label="Name"
              error={!!formErrors.name}
              helperText={formErrors.name ?? " "}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12 }} sx={{ display: "flex" }}>
            <FormControl error={!!formErrors.type} fullWidth>
              <InputLabel id="client-type-label">Client Type</InputLabel>
              <Select
                value={formValues.type?.id ?? ""}
                onChange={handleSelectFieldChange as SelectProps["onChange"]}
                labelId="client-type-label"
                name="type"
                label="Client Type"
                defaultValue={formValues.type?.id ?? ""}
                fullWidth
              >
                {clientTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.description}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{formErrors.type ?? " "}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex" }}>
            <FormControl error={!!formErrors.claimType} fullWidth>
              <InputLabel id="client-claimType-label">Claim Type</InputLabel>
              <Select
                value={formValues.claimType?.id ?? ""}
                onChange={handleSelectFieldChange as SelectProps["onChange"]}
                labelId="client-claimType-label"
                name="claimType"
                label="Claim Type"
                defaultValue={formValues.claimType?.id ?? ""}
                fullWidth
              >
                {claimTypes?.map((claimType) => (
                  <MenuItem key={claimType.id} value={claimType.id}>
                    {claimType.description}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{formErrors.claimType ?? " "}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 6 }}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <FormControl>
              <FormControlLabel
                name="enabled"
                control={
                  <Checkbox
                    size="large"
                    checked={formValues.enabled ?? false}
                    onChange={handleCheckboxFieldChange}
                  />
                }
                label="Enabled"
              />
            </FormControl>
          </Grid>
        </Grid>
      </FormGroup>
    </Box>
  );
};
export default ClientForm;
